<script setup lang="ts">
import DateSpecificHours from '@/assets/date-specific-hours.png'
import {
  UIMultiDatePicker,
  groupConsecutiveDates,
} from '@gohighlevel/ghl-calendar-element'
import {
  Pencil01Icon,
  PlusIcon,
  Trash01Icon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIContentWrap,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UITimePicker,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import moment from 'moment-timezone'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import {
  DatePickerLanguageMapper,
  GHLLanguageKeysMapper,
} from '@/utils/constants'
import { useI18n } from 'vue-i18n'
import AppState from '../../states/app'
import { DayAvailability } from './DayAvailability'
import { Schedule } from './Schedule'

const { t } = useI18n()

moment.locale(GHLLanguageKeysMapper[AppState.locale])

const showOverRideModal = ref(false)
const activeId = ref('')
const listContainerBoxRef = ref<HTMLElement | null>(null)
const itemRefs = ref<HTMLElement[]>([])

const visibleItems = ref<any[]>([])
const showingAll = ref(false)
const containerHeight = ref(680)
let resizeObserver = null as ResizeObserver | null
const overriddenDates = ref<string[]>([])
const originalDatesInEdit = ref<string[]>([]) // Track original dates when editing a group
const showDateSelectionError = ref(false)

const props = defineProps({
  schedule: Schedule,
  isRecurringCalendarSelected: Boolean,
})

const editMode = ref(false)
const overriddenHours = ref([
  {
    startTime: '8:0',
    endTime: '17:0',
  },
])
const customSlotSelectorModal = ref()

const allItems = computed(() => methods.loadData())

const locale = computed(() => {
  return DatePickerLanguageMapper[AppState.locale]
})

// Group consecutive dates with the same hours using shared utility
// Dates are stored as 'YYYY-MM-DD' strings (date-only), so no timezone conversion needed for grouping
const groupedItems = computed(() => {
  return groupConsecutiveDates(allItems.value, locale.value)
})

const remainingItemsCount = computed(
  () => groupedItems.value.length - visibleItems.value.length
)

const onClickAddSpecificHours = () => {
  if (overlappingTimes.value.length > 0 || !props.schedule) {
    return
  }

  if (overriddenDates.value.length === 0) {
    showDateSelectionError.value = true
    return
  }

  // Clear the error message when dates are selected
  showDateSelectionError.value = false

  let updatedRules = [...props.schedule.rules]

  if (editMode.value && originalDatesInEdit.value.length > 0) {
    const formatDate = (dateString: string) =>
      moment(new Date(dateString)).startOf('day').format('YYYY-MM-DD')

    const currentDatesSet = new Set(overriddenDates.value.map(formatDate))

    const originalFormattedDates = originalDatesInEdit.value.map(formatDate)

    // Remove dates that were in the original group but are no longer selected
    originalFormattedDates.forEach(originalDate => {
      if (!currentDatesSet.has(originalDate)) {
        const indexToRemove = updatedRules.findIndex(
          rule => rule.type === 'date' && rule.date === originalDate
        )

        if (indexToRemove !== -1) {
          updatedRules.splice(indexToRemove, 1)
        }
      }
    })
  }

  // Format dates without timezone conversion - dates should remain unchanged
  overriddenDates.value.forEach(dateString => {
    const date = new Date(dateString)
    // Format as YYYY-MM-DD without timezone conversion
    const formattedDate = moment(date).startOf('day').format('YYYY-MM-DD')

    const newRule = new DayAvailability({
      type: 'date',
      date: formattedDate,
      intervals: overriddenHours.value.map(x => ({
        from: x.startTime,
        to: x.endTime,
      })),
      isActive: true,
    })

    const existingRuleIndex = updatedRules.findIndex(
      rule => rule.type === 'date' && rule.date === formattedDate
    )

    if (existingRuleIndex !== -1) {
      // Update the existing rule
      updatedRules = [
        ...updatedRules.slice(0, existingRuleIndex),
        newRule,
        ...updatedRules.slice(existingRuleIndex + 1),
      ]
    } else {
      // Add new rule
      updatedRules = [newRule, ...updatedRules]
    }
  })

  // Update the schedule with the new rules
  props.schedule.setRules(updatedRules)

  showOverRideModal.value = false
  activeId.value = ''
  editMode.value = false
  originalDatesInEdit.value = []
}

const cancelCallback = () => {
  showOverRideModal.value = false
  overriddenDates.value = []
  originalDatesInEdit.value = []
  overriddenHours.value = [
    {
      startTime: '8:0',
      endTime: '17:0',
    },
  ]
  activeId.value = ''
  editMode.value = false
  showDateSelectionError.value = false
}

const methods = {
  openOverrideModal() {
    showOverRideModal.value = true
    editMode.value = false
    overriddenHours.value = [
      {
        startTime: '8:0',
        endTime: '17:0',
      },
    ]
    overriddenDates.value = []
    showDateSelectionError.value = false
  },
  loadData() {
    return props.schedule?.getDateSpecificHours() || []
  },
  async addNextSlots(overriddenHours, item) {
    overriddenHours.push({
      startTime: moment(item.endTime, 'H:m')
        .set({
          hour: moment(item.endTime, 'H:m').hour() + 1,
        })
        .format('H:m'),
      endTime: moment(item.endTime, 'H:m')
        .set({
          hour: moment(item.endTime, 'H:m').hour() + 2,
        })
        .format('H:m'),
    })
    await nextTick()
    customSlotSelectorModal.value.scrollTop =
      customSlotSelectorModal.value.scrollHeight
  },
  async removeHours(index) {
    overriddenHours.value.length > 0
      ? overriddenHours.value.splice(index, 1)
      : (overriddenHours.value = [])
  },

  dateDisabled(date: Date) {
    const selectedDate = moment(date).startOf('day')
    const currentDate = moment().startOf('day')
    return selectedDate.isBefore(currentDate) || false
  },
}

const itemsOverflowContainer = computed(() => {
  if (!itemRefs.value.length || !listContainerBoxRef.value) {
    return false
  }
  const totalItemsHeight = itemRefs.value.reduce(
    (sum, el) => sum + (el?.offsetHeight || 0),
    0
  )
  return totalItemsHeight > listContainerBoxRef.value.clientHeight
})

const invalidTimes = computed(() => {
  return (
    overriddenHours.value.filter(x => {
      if (
        (x.startTime === '0:0' && x.endTime === '0:0') ||
        x.endTime === '0:0'
      ) {
        return false
      }
      return (
        moment(x.startTime, 'H:m').isSameOrAfter(moment(x.endTime, 'H:m')) ||
        moment(x.endTime, 'H:m').isSameOrBefore(moment(x.startTime, 'H:m'))
      )
    }).length > 0
  )
})

const has24hourError = computed(() => {
  overriddenHours.value.forEach(h => {
    if (
      h.startTime === '0:0' &&
      h.endTime === '0:0' &&
      overriddenHours.value.length > 1
    ) {
      return true
    }
  })
  return false
})

const is24HourSelected = computed(() => {
  return overriddenHours.value.length === 1
    ? overriddenHours.value[0].startTime === '0:0' &&
        overriddenHours.value[0].endTime === '0:0'
    : false
})

const overlappingTimes = computed(() => {
  const overlaps = [] as number[]

  if (
    overriddenHours.value.length === 1 &&
    overriddenHours.value[0].startTime === '0:0' &&
    overriddenHours.value[0].endTime === '0:0'
  ) {
    return []
  }

  for (let i = 0; i < overriddenHours.value.length - 1; i++) {
    for (let j = i + 1; j < overriddenHours.value.length; j++) {
      const startTime1 = moment(overriddenHours.value[i].startTime, 'H:mm')
      const endTime1 = moment(overriddenHours.value[i].endTime, 'H:mm')
      const startTime2 = moment(overriddenHours.value[j].startTime, 'H:mm')
      const endTime2 = moment(overriddenHours.value[j].endTime, 'H:mm')

      if (
        startTime1.isBetween(startTime2, endTime2) ||
        endTime1.isBetween(startTime2, endTime2) ||
        startTime2.isBetween(startTime1, endTime1) ||
        endTime2.isBetween(startTime1, endTime1)
      ) {
        overlaps.push(i)
        overlaps.push(j)
      }
    }
  }

  return [...new Set(overlaps)]
})

const handleEdit = (group: {
  dates: DayAvailability[]
  intervals: any[]
  displayText: string
}) => {
  const firstDate = group.dates[0]
  activeId.value = firstDate.date || ''
  editMode.value = true
  overriddenDates.value = group.dates.map(rule => {
    const date = moment(rule.date).startOf('day')
    return date.toISOString()
  })
  originalDatesInEdit.value = [...overriddenDates.value]
  overriddenHours.value = group.intervals.map(x => ({
    startTime: x.from,
    endTime: x.to,
  }))
  showOverRideModal.value = true
}

const handleDeleteGroup = (group: { dates: DayAvailability[] }) => {
  if (!props.schedule) return

  // Remove all dates in the group from the schedule
  let updatedRules = [...props.schedule.rules]
  group.dates.forEach(rule => {
    const index = updatedRules.findIndex(
      r => r.type === 'date' && r.date === rule.date
    )
    if (index !== -1) {
      updatedRules.splice(index, 1)
    }
  })
  props.schedule.setRules(updatedRules)
}

const showAllItems = () => {
  showingAll.value = true
  visibleItems.value = [...groupedItems.value]
}
const showLessItems = () => {
  showingAll.value = false
  calculateVisibleItems()
}
const handleResize = () => {
  if (!showingAll.value) {
    calculateVisibleItems()
  }
}

watch(
  () => props.schedule,
  () => {
    if (!showingAll.value) {
      calculateVisibleItems()
    } else {
      visibleItems.value = [...groupedItems.value]
    }
  },
  { deep: true }
)

watch(
  () => overriddenDates.value,
  () => {
    if (overriddenDates.value.length > 0) {
      showDateSelectionError.value = false
    }
  }
)
const calculateVisibleItems = () => {
  visibleItems.value = []
  let currentHeight = 0
  for (let i = 0; i < groupedItems.value.length; i++) {
    visibleItems.value.push(groupedItems.value[i])
    nextTick(() => {
      if (itemRefs.value && itemRefs.value.length > 0) {
        const lastItemHeight = itemRefs.value[i].offsetHeight
        currentHeight += lastItemHeight

        if (currentHeight > containerHeight.value) {
          visibleItems.value.pop()
          return
        }
        if (
          i < groupedItems.value.length - 1 &&
          currentHeight + 40 > containerHeight.value
        ) {
          visibleItems.value.pop()
        }
      }
    })
  }
}

onMounted(() => {
  nextTick(() => {
    calculateVisibleItems()

    resizeObserver = new ResizeObserver(() => {
      if (!showingAll.value) {
        calculateVisibleItems()
      }
    })

    if (listContainerBoxRef.value) {
      resizeObserver.observe(listContainerBoxRef.value)
    }

    window.addEventListener('resize', handleResize)
  })
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('resize', handleResize)
})

defineExpose({
  methods,
})
</script>
<template>
  <UIContentWrap :locale="GHLLanguageKeysMapper[AppState.locale]" class="!p-0">
    <div class="mb-4 flex flex-col gap-1 text-sm">
      <div class="text-sm font-medium text-gray-700">
        {{ $t('calendar_advanced.availability.specific_hours_button') }}
      </div>
      <p class="text-sm text-gray-500">
        {{ $t('calendar_advanced.availability.specific_hours_description') }}
      </p>
    </div>
    <div class="flex">
      <div v-if="props.isRecurringCalendarSelected">
        <UITooltip placement="top">
          <template #trigger>
            <UIButton
              type="default"
              :disabled="props.isRecurringCalendarSelected"
              @click="methods.openOverrideModal"
            >
              <PlusIcon class="mr-2 h-4 w-4" />
              {{ $t('calendar_advanced.availability.date_specific_level') }}
            </UIButton>
          </template>
          <p class="max-w-sm">
            {{
              $t(
                'settings.availability.schedule.date_specific_hours_recurring_meetings_enabled'
              )
            }}
          </p>
        </UITooltip>
      </div>

      <UIButton
        v-else
        type="default"
        :disabled="props.isRecurringCalendarSelected"
        @click="methods.openOverrideModal"
        class="mb-4"
      >
        <PlusIcon class="mr-2 h-4 w-4" />
        {{ $t('calendar_advanced.availability.date_specific_level') }}
      </UIButton>
    </div>
    <!-- empty state -->
    <div
      v-if="groupedItems.length === 0"
      class="my-20 flex flex-col items-center justify-center"
    >
      <img
        :src="DateSpecificHours"
        class="mb-2 h-36"
        :alt="$t('alt_text.date_specific_hours')"
      />
      <p class="text-md text-center font-semibold text-gray-900">
        {{ $t('calendar_advanced.availability.no_date_specific_heading') }}
      </p>

      <p class="text-center text-xs text-gray-600">
        {{ $t('calendar_advanced.availability.no_date_specific_description') }}
      </p>
    </div>

    <!-- list of date specific hours -->
    <div v-else ref="listContainerBoxRef" class="h-[680px] min-h-[560px]">
      <div
        v-for="(group, index) in visibleItems"
        :key="index"
        ref="itemRefs"
        class="flex cursor-pointer items-start justify-between whitespace-pre-wrap border-b border-gray-200 px-2 py-4"
        :class="{
          'line-through': methods.dateDisabled(
            moment(group.dates[0].date).toDate()
          ),
        }"
        role="button"
      >
        <div class="flex-1">
          <p class="text-sm font-semibold text-gray-700">
            {{ group.displayText }}
          </p>
          <p v-if="group.intervals.length === 0" class="text-sm text-gray-500">
            {{ $t('calendar_advanced.availability.unavailable_label') }}
          </p>
          <p
            v-for="(interval, intervalIndex) in group.intervals"
            v-else
            :key="intervalIndex"
            class="mt-1 text-sm font-normal text-gray-500"
          >
            {{ moment(interval.from, 'HH:mm').format('hh:mm A') }} -
            {{ moment(interval.to, 'HH:mm').format('hh:mm A') }}
          </p>
        </div>
        <UIButton
          id="edit-availability-hour-custom"
          class="px-3 text-gray-600"
          :text="true"
          :aria-label="$t('aria_labels.edit_custom_slot')"
          @click.stop="handleEdit(group)"
        >
          <Pencil01Icon class="w-4" />
        </UIButton>
        <UIButton
          id="remove-availability-hour-custom"
          class="px-3 text-gray-700"
          :text="true"
          :aria-label="$t('aria_labels.remove_custom_slot')"
          @click.stop="handleDeleteGroup(group)"
        >
          <Trash01Icon class="w-4" />
        </UIButton>
      </div>

      <!-- Show 'n more' button if there are hidden items and not expanded -->
      <UIButton
        v-if="!showingAll && remainingItemsCount > 0"
        class="mt-2 w-full"
        text="true"
        @click="showAllItems"
      >
        {{ remainingItemsCount }}
        {{
          $t('settings.availability.schedule.date_specific_hours_show_more') ||
          'more'
        }}
      </UIButton>

      <!-- Show 'Show less' button if expanded -->
      <UIButton
        v-else-if="showingAll && itemsOverflowContainer"
        class="mt-2 w-full"
        text="true"
        @click="showLessItems"
      >
        {{
          $t('settings.availability.schedule.date_specific_hours_show_less') ||
          'Show less'
        }}
      </UIButton>
    </div>
    <UIModal :show="showOverRideModal" :width="440">
      <template #header>
        <div class="flex w-full items-center justify-between">
          <p class="w-3/4 text-[16px] text-gray-900">
            {{ $t('calendar_advanced.availability.subTitle') }}
            <span class="hidden">{{ overriddenDates }}</span>
          </p>
          <XCloseIcon
            class="h-5 w-5 cursor-pointer text-gray-500"
            :aria-label="$t('aria_labels.close_modal')"
            role="button"
            @click="cancelCallback()"
            @keydown="cancelCallback()"
          />
        </div>
      </template>
      <UIModalContent>
        <div
          id="do-picker"
          class="mb-2 flex transform items-center justify-center rounded-lg border-[1px] border-gray-200 py-2"
        >
          <UIMultiDatePicker
            :custom-style="{
              width: '400px',
            }"
            id="date-override picker"
            v-model="overriddenDates"
            :language="locale"
            :panel-view="true"
            :hide-input="true"
            :disabled-dates="methods.dateDisabled"
            :min-date="moment().startOf('day').toDate()"
          />
        </div>
        <p
          v-if="showDateSelectionError"
          class="mb-2 text-left text-sm text-red-600"
        >
          {{ $t('calendar_advanced.availability.please_select_date') }}
        </p>
        <div
          v-if="overriddenDates.length > 0"
          class="rounded-lg border-[1px] border-gray-200 p-4"
        >
          <div class="flex items-center justify-between">
            <p class="mb-3 text-sm font-semibold text-gray-800">
              {{ $t('calendar_advanced.availability.heading_2') }}
            </p>
            <UIButton
              v-if="overriddenHours.length === 0"
              id="add-override-custom"
              :aria-label="$t('aria_labels.add_custom_slot')"
              :text="true"
              class="text-gray-700"
              @click="
                overriddenHours.push({
                  startTime: '8:0',
                  endTime: '17:0',
                })
              "
            >
              <PlusIcon class="w-4" />
            </UIButton>
          </div>

          <div
            v-if="overriddenDates.length > 0"
            ref="customSlotSelectorModal"
            class="max-h-40 overflow-y-auto"
          >
            <div v-if="overriddenHours.length == 0">
              <p class="text-md font-semibold text-gray-600">
                {{ $t('calendar_advanced.availability.unavailable_label') }}
              </p>
            </div>
            <div
              v-for="(item, index) in overriddenHours"
              :key="index"
              class="mb-2 flex items-center gap-2"
            >
              <UITimePicker
                v-model:formatted-value="item.startTime"
                value-format="H:m"
                :minutes="5"
                format="hh:mm a"
                use-12-hours
                :actions="['confirm']"
                class="w-full"
                :status="
                  !item.startTime ||
                  overlappingTimes.includes(index) ||
                  (moment(item.endTime, 'H:m').isSameOrBefore(
                    moment(item.startTime, 'H:m')
                  ) &&
                    item.endTime !== '0:0' &&
                    item.startTime !== '0:0')
                    ? 'error'
                    : 'success'
                "
              />

              <div class="w-8 min-w-max font-semibold capitalize">
                {{ $t('calendar_advanced.availability.to') }}
              </div>
              <UITimePicker
                v-model:formatted-value="item.endTime"
                value-format="H:m"
                :minutes="5"
                format="hh:mm a"
                use-12-hours
                :actions="['confirm']"
                class="w-full"
                :status="
                  !item.endTime ||
                  overlappingTimes.includes(index) ||
                  (moment(item.endTime, 'H:m').isSameOrBefore(
                    moment(item.startTime, 'H:m')
                  ) &&
                    item.endTime !== '0:0' &&
                    item.startTime !== '0:0')
                    ? 'error'
                    : 'success'
                "
              />
              <div class="mr-6 flex items-center justify-around">
                <div class="ml-4 w-4">
                  <UIButton
                    v-if="index === overriddenHours.length - 1"
                    id=""
                    :text="true"
                    class="text-gray-700"
                    :disabled="is24HourSelected"
                    :aria-label="$t('aria_labels.add_custom_slot')"
                    @click="methods.addNextSlots(overriddenHours, item)"
                  >
                    <PlusIcon class="w-4" />
                  </UIButton>
                </div>
                <div class="ml-4 w-4">
                  <UIButton
                    id="add-override-hour-custom"
                    class="text-gray-700"
                    :text="true"
                    :aria-label="$t('aria_labels.remove_custom_slot')"
                    @click="methods.removeHours(index)"
                  >
                    <Trash01Icon class="w-4" />
                  </UIButton>
                </div>
              </div>
            </div>
          </div>
          <p
            v-if="overlappingTimes.length > 0"
            class="text-left text-sm text-red-600"
          >
            {{ $t('calendar_advanced.availability.overlap_error') }}
          </p>
        </div>
      </UIModalContent>
      <template #footer>
        <UIModalFooter
          id="modal-footer"
          positive-text="Submit"
          negative-text="Cancel"
          :disabled="
            overlappingTimes.length > 0 || invalidTimes || has24hourError
          "
          @positive-click="onClickAddSpecificHours"
          @negative-click="cancelCallback"
        />
      </template>
    </UIModal>
  </UIContentWrap>
</template>
<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
<style lang="scss">
#do-picker {
  --primary-600: #344054 !important;

  .n-date-panel-weekdays {
    font-weight: 500 !important;
  }

  .n-date-panel-month__prev {
    margin-left: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__next {
    margin-right: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__fast-next {
    height: 0px !important;
    width: 0px !important;
  }

  .n-date-panel-month__fast-prev {
    height: 0px !important;
    width: 0px !important;
  }

  .n-tabs {
    height: 90%;
    overflow: hidden;
  }

  .n-tab-pane {
    height: 90% !important;
    overflow: scroll !important;
  }
}
</style>
